import {
  Document,
  Editor,
  setTextMeasurer,
  boundsOfNodes,
  type NodeId,
  type Presence,
  type Rect,
  type Vec2,
  type Mat,
  type ShapeKind,
  type Transaction,
  type DocumentJSON,
  type Comment,
  type VotingSession,
} from '@vellum/core';
import { SyncClient, applyRemoteLenient } from './sync.ts';
import { AudioCall } from './audio.ts';
import { ShaderRuntime } from './shaders/runtime.ts';
import { titledNodes, titleRect } from './canvas/overlay.ts';

export type ToolId =
  | 'select'
  | 'lasso'
  | 'hand'
  | 'frame'
  | 'section'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'polygon'
  | 'star'
  | 'pen'
  | 'pencil'
  | 'text'
  | 'sticky'
  | 'shape'
  | 'connector'
  | 'comment'
  | 'stamp'
  | 'eraser'
  | 'mindmap'
  | 'image';

export type Channel = 'doc' | 'selection' | 'camera' | 'ui' | 'peers' | 'tool' | 'history' | 'comments' | 'shaders';

export interface Camera {
  /** screen = page * zoom + (x, y), in CSS pixels */
  x: number;
  y: number;
  zoom: number;
}

export interface Guide {
  axis: 'x' | 'y';
  /** page coordinate of the guide line */
  pos: number;
  from: number;
  to: number;
}

export interface UIState {
  hoverId: NodeId | null;
  marquee: Rect | null;
  /** Lasso selection path being drawn (page coords). */
  lasso: Vec2[] | null;
  guides: Guide[];
  editingTextId: NodeId | null;
  /** a layer being renamed: on its canvas title, or in the layers panel */
  renaming: { id: NodeId; on: 'canvas' | 'layers' } | null;
  editingPathId: NodeId | null;
  /** The in-app AI panel. */
  assistantOpen: boolean;
  /** A link card playing its embed (YouTube, Figma…) in place. */
  playingEmbedId: NodeId | null;
  /** Container the user double-clicked into (selection happens inside it). */
  scopeId: NodeId | null;
  contextMenu: { x: number; y: number } | null;
  /** In-progress drawing preview (pen / pencil / connector). */
  draft: { kind: 'pen' | 'pencil' | 'connector'; points: Vec2[]; closed?: boolean; startId?: NodeId; endId?: NodeId; cursor?: Vec2 } | null;
  /** Drop target highlight when dragging layers into frames. */
  dropTargetId: NodeId | null;
  /** Auto-layout reorder indicator (page-space line). */
  insertLine: { a: Vec2; b: Vec2 } | null;
  showLayers: boolean;
  showProps: boolean;
  shareOpen: boolean;
  /** the people/link sharing dialog (accounts mode) */
  peopleOpen: boolean;
  pluginsOpen: boolean;
  homeOpen: boolean;
  spaceDown: boolean;
  dragging: boolean;
  status: 'connecting' | 'online' | 'offline';
  toast: string | null;
  renderer: 'webgl' | 'canvas2d';
  fps: number;
  /** New comment being composed at a page point. */
  commentDraft: { x: number; y: number; nodeId?: NodeId } | null;
  openCommentId: string | null;
  showResolved: boolean;
  /** Frame being presented (prototype player), or null. */
  presentingId: NodeId | null;
  versionsOpen: boolean;
  shortcutsOpen: boolean;
  figmaTokenOpen: boolean;
  templatesOpen: boolean;
  quickActionsOpen: boolean;
  altDown: boolean;
  /** Dev Mode: inspect, measure and hand off; the canvas doesn't edit. */
  devMode: boolean;
  showGrids: boolean;
  /** ⌘Y: every layer drawn as a thin outline */
  outlineMode: boolean;
  /** I'm spotlighting (everyone follows me) */
  spotlighting: boolean;
  /** Selected anchor (command index) while editing a vector path. */
  pathAnchor: number | null;
  /** Vector edit mode: every selected anchor (command indices), including pathAnchor. */
  pathAnchors: number[];
  /** Collaborator (human or agent) whose view we follow. */
  followId: string | null;
  findOpen: boolean;
  showRulers: boolean;
  /** Guide being dragged (index -1 = new guide from a ruler). */
  draggingGuide: { axis: 'x' | 'y'; index: number; pos: number } | null;
  chatOpen: boolean;
  scriptOpen: boolean;
  /** Image layer in crop mode. */
  croppingId: NodeId | null;
}

export interface ToolOptions {
  shape: ShapeKind;
  stickyColor: string;
  connectorType: 'STRAIGHT' | 'ELBOW' | 'CURVED';
  penColor: string;
  penWidth: number;
  penOpacity: number;
  stamp: string;
  keepTool: boolean;
}

/** Longest side of the copies the server makes (see server imageLevels.ts); beyond that, the original. */
const IMAGE_LEVELS = [128, 256, 512, 1024, 2048];

interface ImageLevelEntry {
  /** decoded but not yet handed to the renderer */
  ready?: { bitmap: ImageBitmap; level: number };
  /** the sharpest level fetched (or handed out) */
  level: number;
  /** the level being fetched */
  loading: number;
  natural?: { width: number; height: number };
}

class ImageCache {
  private map = new Map<string, HTMLImageElement | 'loading' | 'error'>();
  /** Copies sized for the canvas, by src (uploaded images only). */
  private levels = new Map<string, ImageLevelEntry>();
  private fetching = 0;
  private queue: (() => void)[] = [];
  /** Called with the src whose canvas copy got sharper (pick it up with take). */
  onLevel: ((src: string) => void) | null = null;
  constructor(private onLoad: () => void) {}

  /** Images this server stores can be fetched at the size they're shown. */
  leveled(src: string) {
    return src.startsWith('/api/assets/') && !/\.(gif|svg)$/i.test(src);
  }

  /** The original's size, from whichever copy has loaded. */
  naturalSize(src: string): { width: number; height: number } | undefined {
    const full = this.peek(src);
    if (full) return { width: full.naturalWidth, height: full.naturalHeight };
    return this.levels.get(src)?.natural;
  }

  /** Make sure a copy of `src` at least `px` pixels across is loaded or on its way (see take). */
  want(src: string, px: number) {
    let e = this.levels.get(src);
    if (!e) this.levels.set(src, (e = { level: 0, loading: 0 }));
    const natural = e.natural ? Math.max(e.natural.width, e.natural.height) : Infinity;
    // nothing sharper than the original exists
    const need = Math.min(IMAGE_LEVELS.find((l) => l >= px) ?? 4096, IMAGE_LEVELS.find((l) => l >= natural) ?? 4096);
    if (need > e.level && need > e.loading) this.fetchLevel(src, e, need);
  }

  /** A newly decoded copy of `src`, handed over to the caller (who closes it when done). */
  take(src: string): { bitmap: ImageBitmap; level: number } | undefined {
    const e = this.levels.get(src);
    const r = e?.ready;
    if (e) e.ready = undefined;
    return r;
  }

  /** The caller dropped its copy: the next want fetches one again, at the size needed then. */
  forget(src: string) {
    const e = this.levels.get(src);
    if (!e) return;
    e.ready?.bitmap.close();
    e.ready = undefined;
    e.level = 0;
  }

  private fetchLevel(src: string, e: ImageLevelEntry, level: number) {
    e.loading = level;
    const run = async () => {
      this.fetching++;
      try {
        const r = await fetch(`${src}${src.includes('?') ? '&' : '?'}w=${level}`);
        if (!r.ok) throw new Error(String(r.status));
        const w = Number(r.headers.get('x-image-width')),
          h = Number(r.headers.get('x-image-height'));
        // decoded off the main thread
        const bitmap = await createImageBitmap(await r.blob());
        if (w && h) e.natural = { width: w, height: h };
        else e.natural ??= { width: bitmap.width, height: bitmap.height };
        if (level > e.level) {
          e.ready?.bitmap.close();
          e.ready = { bitmap, level };
          e.level = level;
          this.onLevel?.(src);
        } else bitmap.close();
      } catch {
        /* keep what we have */
      } finally {
        if (e.loading === level) e.loading = 0;
        this.fetching--;
        this.queue.shift()?.();
      }
    };
    // a few at a time, so the ones on screen aren't stuck behind the rest
    if (this.fetching < 6) void run();
    else this.queue.push(() => void run());
  }

  get(src: string): CanvasImageSource | undefined {
    const v = this.map.get(src);
    if (v instanceof HTMLImageElement) return v;
    if (v) return undefined;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    this.map.set(src, 'loading');
    img.onload = () => {
      this.map.set(src, img);
      this.onLoad();
    };
    img.onerror = () => this.map.set(src, 'error');
    img.src = src;
    return undefined;
  }
  peek(src: string): HTMLImageElement | undefined {
    const v = this.map.get(src);
    return v instanceof HTMLImageElement ? v : undefined;
  }
}

export function installBrowserMeasurer() {
  const c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(4, 4).getContext('2d')! : document.createElement('canvas').getContext('2d')!;
  setTextMeasurer({
    measure(text, font) {
      c.font = font;
      return c.measureText(text).width;
    },
  });
}

export class App {
  editor: Editor;
  camera: Camera = { x: 0, y: 0, zoom: 1 };
  viewportSize = { width: 800, height: 600 };
  tool: ToolId = 'select';
  toolOptions: ToolOptions = { shape: 'SQUARE', stickyColor: 'yellow', connectorType: 'ELBOW', penColor: '#1e1e1e', penWidth: 4, penOpacity: 1, stamp: '👍', keepTool: false };
  ui: UIState = {
    hoverId: null,
    marquee: null,
    lasso: null,
    guides: [],
    editingTextId: null,
    renaming: null,
    editingPathId: null,
    playingEmbedId: null,
    assistantOpen: false,
    scopeId: null,
    contextMenu: null,
    draft: null,
    dropTargetId: null,
    insertLine: null,
    // phones start with the canvas only; the panels open as sheets
    showLayers: typeof innerWidth === 'undefined' || innerWidth > 760,
    showProps: typeof innerWidth === 'undefined' || innerWidth > 760,
    shareOpen: false,
    peopleOpen: false,
    pluginsOpen: false,
    homeOpen: false,
    spaceDown: false,
    dragging: false,
    status: 'connecting',
    toast: null,
    renderer: 'webgl',
    fps: 0,
    commentDraft: null,
    openCommentId: null,
    showResolved: false,
    presentingId: null,
    versionsOpen: false,
    shortcutsOpen: false,
    figmaTokenOpen: false,
    templatesOpen: false,
    quickActionsOpen: false,
    altDown: false,
    devMode: false,
    showGrids: true,
    outlineMode: false,
    spotlighting: false,
    pathAnchor: null,
    pathAnchors: [],
    followId: null,
    findOpen: false,
    showRulers: false,
    draggingGuide: null,
    chatOpen: false,
    scriptOpen: false,
    croppingId: null,
  };
  /** Last pointer position on the page (for cursor chat). */
  lastPointer: Vec2 | null = null;
  comments: Comment[] = [];
  /** The file's voting session (server state, like comments). */
  voting: VotingSession | null = null;
  /** Recent changes by collaborators (most recent first). */
  activity: { who: string; color: string; label: string; at: number; count: number }[] = [];
  peers = new Map<string, Presence>();
  sync: SyncClient | null = null;
  fileId: string | null = null;
  userName: string;
  /** The signed-in account when the server has accounts turned on. */
  account: { authEnabled: boolean; user: { id: string; email: string; name: string } | null } = { authEnabled: false, user: null };
  /** Your role on the open file; viewers can look, comment and present, not edit. */
  role: 'owner' | 'editor' | 'viewer' = 'owner';
  get readOnly() {
    return this.role === 'viewer';
  }
  private blockedAt = 0;
  images: ImageCache;
  /** Shader fills and effects, run on the GPU in a sandboxed worker. */
  shaders = new ShaderRuntime(() => {
    this.requestRender();
    this.emit('shaders');
  });
  /** Bumped on every document change (cheap cache key). */
  docVersion = 0;
  /** Bumped whenever an image finishes loading. */
  imagesVersion = 0;
  /** Bumped when a font variant (OpenType features) finishes loading. */
  fontsVersion = 0;
  fontsReady() {
    this.fontsVersion++;
    this.docVersion++;
    this.emit('doc');
    this.requestRender();
  }
  private listeners = new Map<Channel, Set<() => void>>();
  private renderListeners = new Set<() => void>();
  private unsubDoc: (() => void) | null = null;
  private cameraByPage = new Map<NodeId, Camera>();
  lastChange: { changed: Set<NodeId>; structural: boolean; keys: Set<string> } | null = null;

  constructor() {
    this.userName = localStorage.getItem('vellum-name') ?? randomName();
    try {
      localStorage.setItem('vellum-name', this.userName);
    } catch {
      /* ignore */
    }
    this.images = new ImageCache(() => {
      this.imagesVersion++;
      this.requestRender();
    });
    this.editor = new Editor(Document.create());
    this.wireEditor();
  }

  get doc(): Document {
    return this.editor.doc;
  }

  // ---------------------------------------------------------------- events

  on(channel: Channel, fn: () => void): () => void {
    let s = this.listeners.get(channel);
    if (!s) this.listeners.set(channel, (s = new Set()));
    s.add(fn);
    return () => s!.delete(fn);
  }

  emit(...channels: Channel[]) {
    for (const c of channels) for (const fn of this.listeners.get(c) ?? []) fn();
  }

  onRender(fn: () => void) {
    this.renderListeners.add(fn);
    return () => this.renderListeners.delete(fn);
  }

  requestRender() {
    for (const fn of this.renderListeners) fn();
  }

  setUI(patch: Partial<UIState>) {
    const prevEditing = this.ui.editingTextId;
    Object.assign(this.ui, patch);
    if ('editingTextId' in patch && prevEditing && prevEditing !== patch.editingTextId) {
      // Leaving text edit: an empty text layer is discarded (like every design tool).
      const n = this.doc.get(prevEditing);
      if (n && n.type === 'TEXT' && !(n.characters ?? '').trim()) this.editor.remove([prevEditing]);
    }
    this.emit('ui');
    this.requestRender();
  }

  toast(msg: string) {
    this.setUI({ toast: msg });
    setTimeout(() => {
      if (this.ui.toast === msg) this.setUI({ toast: null });
    }, 2500);
  }

  setTool(tool: ToolId) {
    if (this.ui.editingTextId && tool !== 'text' && tool !== 'select') this.setUI({ editingTextId: null });
    this.tool = tool;
    this.setUI({ draft: null });
    this.emit('tool');
  }

  // ---------------------------------------------------------------- wiring

  private wireEditor() {
    this.unsubDoc?.();
    const off1 = this.doc.on((ev) => {
      this.docVersion++;
      this.lastChange = { changed: ev.changed, structural: ev.structural, keys: ev.keys };
      if (ev.deleted.size) {
        if (this.ui.editingTextId && ev.deleted.has(this.ui.editingTextId)) this.ui.editingTextId = null;
        if (this.ui.scopeId && ev.deleted.has(this.ui.scopeId)) this.ui.scopeId = null;
      }
      this.emit('doc');
      this.requestRender();
    });
    const off2 = this.editor.on((e) => {
      if (e.type === 'selection') {
        // multi-edit ends when the selection leaves the similar layers
        const roots = this.editor.multiEdit;
        if (roots && (!e.selection.length || e.selection.some((id) => !roots.some((r) => r === id || this.doc.isAncestor(r, id))))) {
          this.editor.multiEdit = null;
          this.emit('ui');
        }
        this.sync?.presence({ selection: e.selection });
        this.emit('selection');
        this.requestRender();
      } else if (e.type === 'page') {
        this.sync?.presence({ pageId: e.pageId, selection: [] });
        const cam = this.cameraByPage.get(e.pageId);
        if (cam) this.setCamera(cam);
        else this.zoomToFit(false);
        this.ui.scopeId = null;
        this.ui.renaming = null;
        this.emit('doc', 'selection', 'ui');
        this.requestRender();
      } else if (e.type === 'history') this.emit('history');
    });
    this.editor.setTransactionHandler((tx: Transaction) => this.sync?.push(tx));
    this.unsubDoc = () => {
      off1();
      off2();
    };
  }

  /** `ready`: connect now, use what arrives once it settles (see SyncOptions.ready). */
  connect(fileId: string | null, ready?: Promise<unknown>) {
    this.sync?.close();
    this.peers.clear();
    this.sync = new SyncClient(fileId, this.userName, {
      onInit: (json: DocumentJSON, _clientId, fid, peers, role) => {
        this.role = role;
        this.loadDocument(json, fid, peers);
      },
      onDenied: (reason) => {
        this.toast(reason);
        this.setUI({ homeOpen: true });
      },
      onRemote: (tx) => {
        // ops that didn't apply, with none of ours in flight to explain it: this copy has drifted
        if (applyRemoteLenient(this.doc, tx) && !this.sync?.busy) this.sync?.resync();
        const p = tx.origin ? this.peers.get(tx.origin) : undefined;
        const who = p?.name ?? (tx.origin?.startsWith('agent:') ? tx.origin.slice(6) : 'Someone');
        const label = tx.label ?? 'Edit';
        const last = this.activity[0];
        if (last && last.who === who && last.label === label && Date.now() - last.at < 4000) {
          last.count++;
          last.at = Date.now();
        } else this.activity.unshift({ who, color: p?.color ?? '#999', label, at: Date.now(), count: 1 });
        this.activity.length = Math.min(this.activity.length, 60);
        this.emit('peers');
      },
      onRtc: (from, data) => void this.audio.onSignal(from, data as never),
      onIce: (servers) => (this.audio.ice = servers),
      onNotice: (message, from) => {
        const p = this.peers.get(from);
        this.toast(p ? `${p.name}: ${message}` : message);
      },
      onPresence: (p) => {
        const prev = this.peers.get(p.clientId);
        this.peers.set(p.clientId, p);
        this.audio.onPresence(p, prev);
        if (p.reaction && p.reaction.at !== prev?.reaction?.at && Date.now() - p.reaction.at < 3000 && p.pageId === this.editor.pageId) this.reactions.push({ ...p.reaction, at: performance.now(), color: p.color });
        // spotlight: follow whoever turns it on (until they stop, or you follow someone else)
        if (p.spotlight && !prev?.spotlight && this.ui.followId !== p.clientId) {
          this.startFollowing(p.clientId);
          this.toast(`${p.name} is spotlighting: you're following their view`);
        } else if (!p.spotlight && prev?.spotlight && this.ui.followId === p.clientId) this.startFollowing(null);
        if (this.ui.followId === p.clientId) this.follow(p, prev);
        this.emit('peers');
        this.requestRender();
      },
      onLeave: (id) => {
        this.peers.delete(id);
        this.audio.onLeave(id);
        this.emit('peers');
        this.requestRender();
      },
      onFocus: (m) => {
        if (m.pageId && m.pageId !== this.editor.pageId && this.doc.get(m.pageId)?.type === 'PAGE') this.editor.setPage(m.pageId);
        const ids = m.ids.filter((id) => this.doc.has(id));
        if (m.select) this.editor.setSelection(ids);
        if (ids.length) this.zoomToNodes(ids, true, true);
      },
      onStatus: (s) => this.setUI({ status: s }),
      onComments: (c) => {
        this.comments = c;
        this.emit('comments');
      },
      onVoting: (v) => {
        const was = this.voting;
        this.voting = v;
        if (v?.active && !was?.active) this.toast(`${v.startedBy} started a vote: you have ${v.votesPerPerson} votes. Click layers to vote.`);
        if (was?.active && v && !v.active) this.toast('Voting ended: see the results');
        this.emit('ui');
        this.requestRender();
      },
    }, { ready });
  }

  private loadDocument(json: DocumentJSON, fileId: string, peers: Presence[]) {
    if (!performance.getEntriesByName('vellum:document').length) performance.mark('vellum:document');
    const prevPage = this.editor.pageId;
    const prevSel = this.editor.selection;
    const firstLoad = this.fileId !== fileId;
    this.fileId = fileId;
    const url = new URL(location.href);
    if (url.searchParams.get('file') !== fileId) {
      url.searchParams.set('file', fileId);
      history.replaceState(null, '', url);
    }
    this.editor = new Editor(Document.fromJSON(json), { clientId: this.sync?.clientId });
    this.editor.readOnly = this.readOnly;
    this.editor.onBlocked = () => {
      if (Date.now() - this.blockedAt > 2500) this.toast('You can view and comment on this file. Ask the owner for edit access.');
      this.blockedAt = Date.now();
    };
    if (this.readOnly) this.tool = 'select';
    this.wireEditor();
    if (!firstLoad && this.doc.get(prevPage)?.type === 'PAGE') {
      this.editor.setPage(prevPage);
      this.editor.setSelection(prevSel.filter((id) => this.doc.has(id)));
    }
    this.peers = new Map(peers.map((p) => [p.clientId, p]));
    document.title = `${this.doc.must(this.doc.rootId).name} – Vellum`;
    this.docVersion++;
    if (firstLoad) this.zoomToFit(false);
    this.sync?.presence({ pageId: this.editor.pageId, viewport: this.viewportRect(), selection: this.editor.selection });
    this.emit('doc', 'selection', 'peers', 'ui', 'history');
    this.requestRender();
  }

  /** Keep our view on a followed collaborator: their viewport (humans) or their work (agents). */
  private follow(p: Presence, prev?: Presence) {
    if (p.pageId && p.pageId !== this.editor.pageId && this.doc.get(p.pageId)?.type === 'PAGE') this.editor.setPage(p.pageId);
    if (p.kind === 'human' && p.viewport) {
      const v = p.viewport;
      const z = Math.min(this.viewportSize.width / v.width, this.viewportSize.height / v.height);
      this.setCamera({ zoom: z, x: this.viewportSize.width / 2 - (v.x + v.width / 2) * z, y: this.viewportSize.height / 2 - (v.y + v.height / 2) * z });
    } else if (p.kind === 'agent') {
      const sel = (p.selection ?? []).filter((id) => this.doc.has(id));
      if (sel.length && sel.join() !== (prev?.selection ?? []).join()) this.zoomToNodes(sel, true);
    }
  }

  /** The file's audio call. */
  audio = new AudioCall({
    me: () => this.sync?.clientId ?? null,
    people: () => this.peers,
    presence: (p) => this.sync?.presence(p),
    signal: (to, data) => this.sync?.rtc(to, data),
    changed: () => this.emit('peers'),
    toast: (m) => this.toast(m),
  });

  // Dev Mode code generators from a running plugin (figma.codegen)
  codegen: { name: string; frame: HTMLIFrameElement } | null = null;
  codegenResults = new Map<NodeId, { title: string; language: string; code: string }[]>();
  setCodegen(c: { name: string; frame: HTMLIFrameElement } | null) {
    this.codegen = c;
    this.codegenResults.clear();
    this.emit('ui');
  }
  /** Ask the plugin for code for a layer; the answer arrives through codegenResult. */
  requestCodegen(id: NodeId, language = 'TYPESCRIPT') {
    this.codegen?.frame.contentWindow?.postMessage({ __vellum: 'codegen', id, language }, '*');
  }
  codegenResult(id: NodeId, results: { title: string; language: string; code: string }[]) {
    this.codegenResults.set(id, results);
    this.emit('ui');
  }

  /** Emoji reactions in flight (local times), drawn by the overlay. */
  reactions: { emoji: string; x: number; y: number; at: number; color: string }[] = [];

  react(emoji: string) {
    const p = this.lastPointer ?? this.screenToPage({ x: this.viewportSize.width / 2, y: this.viewportSize.height / 2 });
    const r = { emoji, x: p.x, y: p.y, at: Date.now() };
    this.reactions.push({ ...r, at: performance.now(), color: '#0d99ff' });
    this.sync?.presence({ reaction: r });
    this.requestRender();
  }

  setSpotlight(on: boolean) {
    this.setUI({ spotlighting: on });
    this.sync?.presence({ spotlight: on });
    this.toast(on ? 'Everyone is now following your view' : 'Spotlight ended');
  }

  startFollowing(clientId: string | null) {
    this.setUI({ followId: clientId });
    const p = clientId ? this.peers.get(clientId) : null;
    if (p) this.follow(p);
  }

  // ---------------------------------------------------------------- camera

  setCamera(c: Camera) {
    this.camera = { ...c, zoom: clampZoom(c.zoom) };
    this.cameraByPage.set(this.editor.pageId, this.camera);
    this.sync?.presence({ viewport: this.viewportRect() });
    this.emit('camera');
    this.requestRender();
  }

  screenToPage(p: Vec2): Vec2 {
    return { x: (p.x - this.camera.x) / this.camera.zoom, y: (p.y - this.camera.y) / this.camera.zoom };
  }

  pageToScreen(p: Vec2): Vec2 {
    return { x: p.x * this.camera.zoom + this.camera.x, y: p.y * this.camera.zoom + this.camera.y };
  }

  /** page -> CSS pixel matrix */
  viewMatrix(): Mat {
    const { x, y, zoom } = this.camera;
    return [zoom, 0, 0, zoom, x, y];
  }

  viewportRect(): Rect {
    const a = this.screenToPage({ x: 0, y: 0 });
    return { x: a.x, y: a.y, width: this.viewportSize.width / this.camera.zoom, height: this.viewportSize.height / this.camera.zoom };
  }

  zoomAt(factor: number, screen: Vec2) {
    const z = clampZoom(this.camera.zoom * factor);
    const p = this.screenToPage(screen);
    this.setCamera({ zoom: z, x: screen.x - p.x * z, y: screen.y - p.y * z });
  }

  setZoom(z: number) {
    this.zoomAt(z / this.camera.zoom, { x: this.viewportSize.width / 2, y: this.viewportSize.height / 2 });
  }

  zoomToRect(r: Rect, maxZoom = 1, animate = false) {
    const pad = 80;
    const vw = Math.max(100, this.viewportSize.width - pad * 2);
    const vh = Math.max(100, this.viewportSize.height - pad * 2);
    const z = clampZoom(Math.min(maxZoom, vw / Math.max(1, r.width), vh / Math.max(1, r.height)));
    const target = { zoom: z, x: this.viewportSize.width / 2 - (r.x + r.width / 2) * z, y: this.viewportSize.height / 2 - (r.y + r.height / 2) * z };
    if (!animate) return this.setCamera(target);
    const from = { ...this.camera };
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / 320);
      const e = 1 - Math.pow(1 - k, 3);
      // interpolate in log-zoom space for a natural feel
      const zoom = Math.exp(Math.log(from.zoom) + (Math.log(target.zoom) - Math.log(from.zoom)) * e);
      const cx0 = (this.viewportSize.width / 2 - from.x) / from.zoom;
      const cy0 = (this.viewportSize.height / 2 - from.y) / from.zoom;
      const cx1 = r.x + r.width / 2,
        cy1 = r.y + r.height / 2;
      const cx = cx0 + (cx1 - cx0) * e,
        cy = cy0 + (cy1 - cy0) * e;
      this.setCamera({ zoom, x: this.viewportSize.width / 2 - cx * zoom, y: this.viewportSize.height / 2 - cy * zoom });
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /** Rename a layer in place: on its canvas title when one is on screen, otherwise in the layers panel. */
  startRename(id: NodeId | undefined = this.editor.selection[0]) {
    const n = id ? this.doc.get(id) : undefined;
    if (!n || this.readOnly || n.type === 'PAGE' || n.type === 'DOCUMENT') return;
    const r = titledNodes(this).some((t) => t.id === n.id) ? titleRect(this, n) : null;
    const visible = !!r && r.x >= 0 && r.y >= 0 && r.x + r.width <= this.viewportSize.width && r.y + r.height <= this.viewportSize.height;
    this.setUI({ editingTextId: null, renaming: { id: n.id, on: visible ? 'canvas' : 'layers' } });
  }

  zoomToNodes(ids: NodeId[], animate = true, onlyIfOffscreen = false) {
    const b = boundsOfNodes(this.doc, ids);
    if (!b) return;
    if (onlyIfOffscreen) {
      const v = this.viewportRect();
      // "on screen" leaves out the edges and the floating toolbar along the bottom
      const m = 16 / this.camera.zoom;
      const toolbar = 72 / this.camera.zoom;
      if (b.x >= v.x + m && b.y >= v.y + m && b.x + b.width <= v.x + v.width - m && b.y + b.height <= v.y + v.height - toolbar) return;
    }
    this.zoomToRect(b, Math.max(this.camera.zoom, 1), animate);
  }

  zoomToFit(animate = true) {
    const kids = this.doc.children(this.editor.pageId).map((n) => n.id);
    if (!kids.length) return this.setCamera({ zoom: 1, x: this.viewportSize.width / 2, y: this.viewportSize.height / 2 });
    this.zoomToRect(boundsOfNodes(this.doc, kids)!, 1, animate);
  }

  // ---------------------------------------------------------------- misc

  pageKind(): 'DESIGN' | 'WHITEBOARD' {
    return this.doc.get(this.editor.pageId)?.pageKind ?? 'DESIGN';
  }

  /** Upload a video and a poster frame for it (the canvas shows the poster; prototypes play the video). */
  async uploadVideo(file: Blob): Promise<{ video: string; poster: string; width: number; height: number }> {
    const resp = await fetch(`/api/assets`, { method: 'POST', headers: { 'content-type': file.type || 'video/mp4' }, body: file });
    if (!resp.ok) throw new Error('Could not upload the video');
    const video = ((await resp.json()) as { url: string }).url;
    const el = document.createElement('video');
    el.muted = true;
    el.playsInline = true;
    el.src = URL.createObjectURL(file);
    await new Promise<void>((res, rej) => {
      el.onloadeddata = () => res();
      el.onerror = () => rej(new Error('This video format can’t be played here'));
    });
    el.currentTime = Math.min(0.1, el.duration || 0);
    await new Promise((res) => (el.onseeked = res));
    const c = document.createElement('canvas');
    c.width = el.videoWidth || 640;
    c.height = el.videoHeight || 360;
    c.getContext('2d')!.drawImage(el, 0, 0, c.width, c.height);
    URL.revokeObjectURL(el.src);
    const blob = await new Promise<Blob>((res) => c.toBlob((b) => res(b!), 'image/jpeg', 0.85));
    const { src } = await this.uploadImage(blob);
    return { video, poster: src, width: c.width, height: c.height };
  }

  async uploadImage(file: Blob): Promise<{ src: string; width: number; height: number }> {
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    let src = dataUrl;
    try {
      const resp = await fetch(`/api/assets`, { method: 'POST', headers: { 'content-type': file.type || 'application/octet-stream' }, body: file });
      if (resp.ok) src = ((await resp.json()) as { url: string }).url;
    } catch {
      /* fall back to inline data URL */
    }
    const img = new Image();
    await new Promise((res) => {
      img.onload = res;
      img.onerror = res;
      img.src = dataUrl;
    });
    return { src, width: img.naturalWidth || 200, height: img.naturalHeight || 200 };
  }
}

function clampZoom(z: number) {
  return Math.max(0.01, Math.min(256, z));
}

const ADJ = ['Swift', 'Bright', 'Calm', 'Bold', 'Clever', 'Lucky', 'Quiet', 'Sunny'];
const ANIMAL = ['Otter', 'Fox', 'Heron', 'Lynx', 'Panda', 'Koala', 'Falcon', 'Badger'];
function randomName() {
  return `${ADJ[Math.floor(Math.random() * ADJ.length)]} ${ANIMAL[Math.floor(Math.random() * ANIMAL.length)]}`;
}

export const app = new App();
(globalThis as unknown as { vellum: App }).vellum = app;
